import { SplashScreen } from '@capacitor/splash-screen';

export class CapacitorUtils {

  static isNative(): boolean {
    return typeof window !== 'undefined' && (window as any).Capacitor?.isNativePlatform?.();
  }

  static async initializeApp(): Promise<void> {
    if (!this.isNative()) return;

    try {
      const Cap = (window as any).Capacitor;

      // Set status bar style and overlay settings
      try {
        const modStatus = '@capacitor/' + 'status-bar';
        const status = await import(/* @vite-ignore */ modStatus);
        const platform = Cap?.getPlatform?.();
        if (platform === 'android' || platform === 'ios') {
          // Set status bar to not overlay the webview
          await status.StatusBar.setOverlaysWebView({ overlay: false });
          // Set status bar style
          await status.StatusBar.setStyle({ style: status.Style.Dark });
          // Set background color for Android
          if (platform === 'android') {
            await status.StatusBar.setBackgroundColor({ color: '#ffffff' });
          }
        }
      } catch { }

      // Show keyboard accessory bar (only on iOS)
      try {
        const modKeyboard = '@capacitor/' + 'keyboard';
        const keyboard = await import(/* @vite-ignore */ modKeyboard);
        const platform = Cap?.getPlatform?.();
        if (platform === 'ios') {
          await keyboard.Keyboard.setAccessoryBarVisible({ isVisible: true });
        }
      } catch { }

      console.log('Capacitor app initialized successfully');
    } catch (error) {
      console.warn('Some Capacitor features not available:', error);
    }
  }

  /** Hide the native launch splash after the first React render. */
  static async hideSplashScreen(): Promise<void> {
    if (!this.isNative()) return;

    try {
      await SplashScreen.hide();
    } catch (error) {
      console.warn('Unable to hide native splash screen:', error);
    }
  }

  static async getDeviceInfo() {
    if (!this.isNative()) return null;

    try {
      const modDevice = '@capacitor/' + 'device';
      const device = await import(/* @vite-ignore */ modDevice);
      const info = await device.Device.getInfo();
      return info;
    } catch (error) {
      console.error('Error getting device info:', error);
      return null;
    }
  }

  static async getNetworkStatus() {
    if (!this.isNative()) return { connected: true, connectionType: 'wifi' };

    try {
      const modNet = '@capacitor/' + 'network';
      const net = await import(/* @vite-ignore */ modNet);
      const status = await net.Network.getStatus();
      return status;
    } catch (error) {
      console.error('Error getting network status:', error);
      return { connected: true, connectionType: 'unknown' };
    }
  }

  static addNetworkListener(callback: (status: { connected: boolean; connectionType: string }) => void) {
    if (!this.isNative()) return;
    {
      const modNet = '@capacitor/' + 'network';
      import(/* @vite-ignore */ modNet)
        .then(({ Network }) => {
          Network.addListener('networkStatusChange', callback);
        })
        .catch(() => { });
    }
  }

  static async setStatusBarColor(color: string) {
    if (!this.isNative()) return;

    try {
      const modStatus = '@capacitor/' + 'status-bar';
      const status = await import(/* @vite-ignore */ modStatus);
      await status.StatusBar.setBackgroundColor({ color });
    } catch (error) {
      console.error('Error setting status bar color:', error);
    }
  }

  static async setStatusBarStyle(style: 'light' | 'dark') {
    if (!this.isNative()) return;

    try {
      const modStatus = '@capacitor/' + 'status-bar';
      const status = await import(/* @vite-ignore */ modStatus);
      await status.StatusBar.setStyle({
        style: style === 'light' ? status.Style.Light : status.Style.Dark,
      });
    } catch (error) {
      console.error('Error setting status bar style:', error);
    }
  }
}
